/**
 * This file exports functions related to product prices:
 *
 *  Retail price: getProductRetailPrice, getProductRetailPriceWithTax
 *  	This is the price at which this product is usually sold in shops,
 *  	or the recommended price from the manufacturer. We show this striked-through
 *  	if it’s higher than the price we sell the produce at, otherwise we don’t display it.
 *
 *  Regular price: getProductRegularPrice, getProductRegularPriceWithTax
 *  	This is the regular price of the product, which is the price we sell it at
 *   	unless we do some special offer.
 *
 *  Special price: getProductSpecialPrice, getProductSpecialPriceWithTax
 *  	This is an optional special price of the product, which we set when we do a special offer.
 *
 *  Final price: getProductFinalPrice, getProductFinalPriceWithTax
 *    This is the effective price of the product. It’s the minimum of the regular and the special price.
 *
 *  Tier prices: getProductTierPrices,
 *               getProductTierPrice, getProductTierPriceWithTax
 *  	This is an array of prices for larger quantities of the product.
 *  	Each item of the array defines a quantity with a price (with and without tax)
 */

import { datetimeToDate } from './dates'
import type { TierPrice } from '$lib/js/elasticsearch/types'
import { getFullLocale, type SiteScope } from './i18n'
import { truncateToDecimals } from '$lib/js/helpers/maths'

/**
 * Maximum number of decimals for a price.
 * This is needed to avoid rounding errors where 29.9596 != 29.9596000002
 **/
export const PRICE_DECIMALS = 4

type WithTaxRate = { tax_rate?: number, tax_class_id: number }
type WithTierPrices = { tier_prices?: TierPrice[] }

export function getProductRetailPrice(product: {
	retail_price_es: number | null,
	// retail_price_fr: number | null,
	price: number
}) {
	// TODO[i18n]: (BF) find a way to use the right retail_price_${scope} key. For all other keys it's
	//   fine because the index is already scoped, but this key comes from _extra which is not, so both
	//   the French and Spanish indices have retail_price_es and retail_price_fr.
	//   We may want to have both global and scoped _extra indices.
	//
	//   Same thing in getProductRetailPriceWithTax.

	// Default on the regular price
	return product.retail_price_es || getProductRegularPrice(product)
}

/** Equivalent of `getProductRetailPrice` with tax included. */
export function getProductRetailPriceWithTax(product: {
	retail_price_es: number | null,
	// retail_price_fr: number | null,
	price_with_tax: number,
	sku: string
} & WithTaxRate) {
	// TODO[i18n]: see the comment in getProductRetailPrice for the field
	if (product.retail_price_es) {
		return applyTax(product.retail_price_es, product)
	}

	// Default on the regular price
	return getProductRegularPriceWithTax(product)
}

export function getProductRegularPrice(product: { price: number }) {
	return product.price
}


/** Equivalent of `getProductRegularPrice` with tax included. */
export function getProductRegularPriceWithTax(product: {
	price_with_tax: number
} | {
	price_with_tax: undefined
	// Those are used only if price_with_tax is not set
	price: number,
	tax_rate?: number,
	tax_class_id: number,
	sku: string,
}) {
	if (product.price_with_tax !== undefined) {
		return product.price_with_tax
	}

	reportMissingAttribute('price_with_tax', product)

	return applyTax(product.price, product)
}

/** Return a product's special price. Return `NaN` if it doesn’t have any. */
export function getProductSpecialPrice(product: { special_price?: number }) {
	return product.special_price || NaN
}


/** Equivalent of `getProductSpecialPrice` with tax included. */
export function getProductSpecialPriceWithTax(product: {
	special_price_with_tax: number
	special_from_date?: string,
	special_to_date?: string
} | {
	special_price_with_tax: undefined
	// Those are used only if special_price_with_tax is undefined
	special_price: number,
	sku: string,
	special_from_date?: string,
	special_to_date?: string
	tax_rate?: number
	tax_class_id: number
}) {
	if (!isPromotion(product)) {
		return NaN
	}

	if (product.special_price_with_tax !== undefined) {
		return product.special_price_with_tax
	}
	if (product.special_price) {
		// This should not happen -- remove this code if we don't get reports in Sentry
		reportMissingAttribute('special_price_with_tax', product)

		return applyTax(product.special_price, product)
	}

	return NaN
}

function determineFinalPrice(regular_price: number, special_price: number, tier_price: number) {
	let price = regular_price

	if (special_price && special_price <= price) {
		price = special_price
	}

	if (tier_price && tier_price <= price) {
		price = tier_price
	}

	return price
}

export function getPercentageSaved(retail_price: number, regular_price: number, special_price: number) {
	let percentage_saved: string | null = null

	if (special_price && special_price <= regular_price) {
		percentage_saved = (100 - (special_price * 100 / regular_price)).toFixed(2)
	}

	if (regular_price < retail_price) {
		percentage_saved = (100 - (regular_price * 100 / retail_price)).toFixed(2)
	}

	return percentage_saved
}

export function getProductFinalPrice(product: {
	price: number,
	special_price?: number,
	tier_prices?: TierPrice[]
}, quantity = 1) {
	return determineFinalPrice(
		getProductRegularPrice(product),
		getProductSpecialPrice(product),
		getProductTierPrice(product, quantity),
	)
}


/** Equivalent of `getProductFinalPrice` with tax included. */
export function getProductFinalPriceWithTax(product: {
	sku: string,
	price_with_tax: number,
	special_price_with_tax?: number,
	special_to_date?: string,
	special_from_date?: string,
	tier_prices?: TierPrice[],

	tax_rate?: number,
	tax_class_id?: number,
}, quantity = 1) {
	return determineFinalPrice(
		getProductRegularPriceWithTax(product),
		// @ts-expect-error TS2345
		getProductSpecialPriceWithTax(product),
		getProductTierPriceWithTax(product, quantity),
	)

}

export function getProductTierPrices(product: WithTierPrices): TierPrice[] | undefined {
	return product.tier_prices
}


export function getProductTierPrice(product: WithTierPrices, quantity: number, with_tax = false) {
	const tier_prices = getProductTierPrices(product)
	let price = NaN

	if (tier_prices !== undefined) {
		for (const tier_price of tier_prices) {
			if (quantity >= tier_price.qty) {
				price = with_tax ? tier_price.value_with_tax : tier_price.value
			} else {
				break
			}
		}
	}
	return price
}


export function getProductTierPriceWithTax(product: WithTierPrices, quantity: number) {
	return getProductTierPrice(product, quantity, true)
}

/**
 * Test if a product has a promotion.
 */
export function isPromotion(product: { special_from_date?: string, special_to_date?: string },
														// Used for tests only
														current_date?: Date) {
	if (!product?.special_from_date || !product?.special_to_date) {
		return false
	}

	const startDate = new Date(product.special_from_date)
	const endDate = new Date(product.special_to_date)

	if (current_date === undefined) {
		current_date = new Date()
		current_date.setHours(0, 0, 0, 0) // TODO: why do we need this?
	}

	return current_date >= startDate && current_date <= endDate
}

/**
 * Return the end of the promotion on a product as a date string,
 * or `false` if the date range is invalid.
 *
 * @see isPromotion
 */
export function getPromotionEndDate(product: { special_from_date?: string, special_to_date?: string }) {
	if (product.special_to_date && isPromotion(product)) {
		// TODO[i18n] set scope
		return datetimeToDate(product.special_to_date, { scope: 'es' })
	}
	return false
}

// TODO[i18n] update scope automatically
export function getFormattedPrice(price: number, scope: SiteScope = 'es') {
	return price.toLocaleString(getFullLocale(scope), {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

// Internals //

/**
 * Apply the tax rate of a product to a price.
 */
function applyTax(price: number, product: { sku: string } & WithTaxRate): number {
	if (product.tax_rate !== undefined) {
		const tax_multiplier = 1 + (product.tax_rate / 100)

		return truncateToDecimals(price * tax_multiplier, PRICE_DECIMALS)
	}

	reportMissingAttribute('tax_rate', product)

	// Legacy behavior for products without tax_rate
	// (cross-sell/upsell/related should progressively get them in Dec 2024)
	switch (product.tax_class_id) {
		case 10:
			return price * 1.21

		case 11:
			return price * 1.1

		case 12:
			return price * 1.04

		case 14:
			return price

		case 15:
			return price * 1.05

		default:
			throw new Error(`Unknown tax_class_id ${product.tax_class_id}`)
	}
}

function reportMissingAttribute(attribute: string, product: { sku: string }) {
	// Don't report this to Sentry not to overload our quota
	// Sentry.captureMessage(`Product has no ${attribute}`, { tags: { sku: product.sku }, level: 'warning' })
	console.warn(`Product ${product.sku} has no ${attribute}`)
}
